"use client";

import { useMemo } from "react";
import { cn } from "@/lib/utils";
import { Slider } from "@/components/ui/slider";

export const CONDITION_GRADE_MIN = 1;
export const CONDITION_GRADE_MAX = 10;

export const CONDITION_GRADE_LABELS: Record<number, string> = {
  1: "For Parts",
  2: "Heavily Damaged",
  3: "Damaged",
  4: "Worn",
  5: "Fair",
  6: "Good",
  7: "Very Good",
  8: "Excellent",
  9: "Near Mint",
  10: "Mint",
};

type GradeRangeFilterPanelProps = {
  value: [number, number];
  min: number;
  max: number;
  step?: number;
  onValueChange: (value: [number, number]) => void;
  onClear?: () => void;
  clearLabel?: string;
  showClear?: boolean;
  hint?: string;
  labels?: Record<number, string>;
  valueSuffix?: string;
  showTickLabels?: boolean;
  clearButtonClassName?: string;
  className?: string;
};

function formatGradeValue(value: number, suffix?: string) {
  const formatted = Number.isInteger(value) ? String(value) : String(value);
  return suffix ? `${formatted}${suffix}` : formatted;
}

function lookupGradeLabel(labels: Record<number, string> | undefined, value: number) {
  if (!labels) return undefined;
  return labels[value] ?? labels[Math.round(value)];
}

function buildIntegerTicks(min: number, max: number) {
  const ticks: number[] = [];
  for (let index = Math.ceil(min); index <= Math.floor(max); index += 1) {
    ticks.push(index);
  }
  return ticks;
}

export function GradeRangeFilterPanel({
  value,
  min,
  max,
  step = 1,
  onValueChange,
  onClear,
  clearLabel = "Clear",
  showClear = true,
  hint,
  labels,
  valueSuffix = "/10",
  showTickLabels = true,
  clearButtonClassName,
  className,
}: GradeRangeFilterPanelProps) {
  const ticks = useMemo(
    () => (showTickLabels ? buildIntegerTicks(min, max) : []),
    [max, min, showTickLabels],
  );

  return (
    <div className={cn("space-y-4", className)}>
      {hint || (showClear && onClear) ? (
        <div className="flex items-center justify-between gap-3 px-1">
          {hint ? (
            <p className="min-w-0 flex-1 text-sm text-neutral-400">{hint}</p>
          ) : (
            <span className="flex-1" aria-hidden />
          )}
          {showClear && onClear ? (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onClear();
              }}
              className={cn(
                "shrink-0 text-xs font-semibold text-primary hover:text-primary/80",
                clearButtonClassName,
              )}
            >
              {clearLabel}
            </button>
          ) : null}
        </div>
      ) : null}

      <div className="flex items-start gap-3 px-1">
        <div className="min-w-0 flex-1">
          <p className="text-xl font-semibold text-white">{formatGradeValue(value[0], valueSuffix)}</p>
          {lookupGradeLabel(labels, value[0]) ? (
            <p className="mt-0.5 text-sm text-neutral-400">{lookupGradeLabel(labels, value[0])}</p>
          ) : null}
        </div>
        <span className="pt-1 text-sm text-neutral-500">—</span>
        <div className="min-w-0 flex-1 text-right">
          <p className="text-xl font-semibold text-white">{formatGradeValue(value[1], valueSuffix)}</p>
          {lookupGradeLabel(labels, value[1]) ? (
            <p className="mt-0.5 text-sm text-neutral-400">{lookupGradeLabel(labels, value[1])}</p>
          ) : null}
        </div>
      </div>

      <div className="px-1">
        <Slider
          tone="panel"
          value={value}
          min={min}
          max={max}
          step={step}
          minStepsBetweenThumbs={0}
          onValueChange={onValueChange}
          className="py-3"
        />
        {showTickLabels && ticks.length > 0 ? (
          <div className="mt-2 flex justify-between px-0.5">
            {ticks.map((tick) => {
              const active = tick >= value[0] && tick <= value[1];
              return (
                <span
                  key={tick}
                  className={cn(
                    "w-0 flex-1 text-center text-[10px] font-medium tabular-nums",
                    active ? "text-white" : "text-neutral-500",
                  )}
                >
                  {tick}
                </span>
              );
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
}
